/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { Role } from "App/Models";

/* A single right (a boolean column on Role). "Implies" lists the rights it forces on (cascade):
   Update/Create imply Read. */
export interface Right {
    Key: keyof Role;
    Label: string;
    Implies: ( keyof Role )[];
}

/* A resource groups the rights the UI shows together (Plan, Coupon, Customer, Ads publication). */
export interface Resource {
    Id: string;
    Label: string;
    Rights: Right[];
}

/* v1 resources. Order is the display order in the editor. Read is always listed first so the
   cascade reads naturally (the broader right sits below and ticks Read above it). */
function GetResources(): Resource[] {
    return [
        {
            Id: "Plan",
            Label: $t( "ROLES_resourcePlan" ),
            Rights: [
                { Key: "PlanRead", Label: $t( "ROLES_rightRead" ), Implies: [] },
                { Key: "PlanUpdate", Label: $t( "ROLES_rightUpdate" ), Implies: [ "PlanRead" ] }
            ]
        },
        {
            Id: "Coupon",
            Label: $t( "ROLES_resourceCoupon" ),
            Rights: [
                { Key: "CouponRead", Label: $t( "ROLES_rightRead" ), Implies: [] },
                { Key: "CouponCreate", Label: $t( "ROLES_rightCreate" ), Implies: [ "CouponRead" ] }
            ]
        },
        {
            Id: "Customer",
            Label: $t( "ROLES_resourceCustomer" ),
            Rights: [
                { Key: "CustomerRead", Label: $t( "ROLES_rightRead" ), Implies: [] },
                {
                    Key: "CustomerCreate",
                    Label: $t( "ROLES_rightCreate" ),
                    Implies: [ "CustomerRead" ]
                }
            ]
        },
        {
            Id: "AdsPublication",
            Label: $t( "ROLES_resourceAdsPublication" ),
            Rights: [
                { Key: "AdsPublicationRead", Label: $t( "ROLES_rightRead" ), Implies: [] },
                {
                    Key: "AdsPublicationCreate",
                    Label: $t( "ROLES_rightCreate" ),
                    Implies: [ "AdsPublicationRead" ]
                }
            ]
        }
    ];
}

/* Every privilege column, flattened — used to reset a role and to count granted rights. */
function AllRightKeys(): ( keyof Role )[] {
    const keys: ( keyof Role )[] = [];

    for( const resource of GetResources() )
        for( const right of resource.Rights )
            keys.push( right.Key );

    return keys;
}

/* Enforce the cascade in place on a role after a single right is toggled.
   - turning a right ON forces every right it implies ON (Update ⇒ Read);
   - turning a right OFF turns OFF every right that implies it (no Read ⇒ no Update). */
function ApplyCascade( role: Role, key: keyof Role, value: boolean ): void {
    ( role as any )[ key ] = value;

    const resources = GetResources();

    if( value ) {
        for( const resource of resources )
            for( const right of resource.Rights )
                if( right.Key === key )
                    for( const implied of right.Implies )
                        ( role as any )[ implied ] = true;

        return;
    }

    for( const resource of resources )
        for( const right of resource.Rights )
            if( right.Implies.includes( key ) )
                ( role as any )[ right.Key ] = false;
}

/* True when the right cannot be unticked on its own because a broader granted right requires it
   (e.g. Read while Update is on). The UI disables such a checkbox. */
function IsLocked( role: Role, key: keyof Role ): boolean {
    for( const resource of GetResources() )
        for( const right of resource.Rights )
            if(
                right.Implies.includes( key ) &&
                ( role as any )[ right.Key ] === true
            )
                return true;

    return false;
}

function GrantedCount( role: Role ): number {
    return AllRightKeys().filter(
        ( key ): boolean => ( role as any )[ key ] === true ).length;
}

export default {
    GetResources,
    AllRightKeys,
    ApplyCascade,
    IsLocked,
    GrantedCount
};
